import APITemplate from '@/src/api/APITemplate'
import { endpoint } from '@modules/tarefas/constants/URL'
import con from '@services/connection'

/**
 * ApiSprints — a camada de API da LISTA (sprint) e dos Modelos de Lista.
 *
 * Existe separada de `apiTarefas` porque lista e tarefa são recursos distintos:
 * a lista é o contêiner temporal dentro da pasta, a tarefa é a unidade de
 * trabalho dentro dela. Tudo o que é escopado por `sprintId` mora aqui —
 * inclusive o CRUD que antes vivia em `apiTarefas` — para que a fronteira seja
 * uma só; sprint em dois arquivos seria pior que sprint no arquivo errado.
 *
 * Modelo de Lista (`list-templates`) entra junto porque só existe em função da
 * lista: um modelo é a estrutura de uma lista guardada para reaproveitar, e as
 * duas pontas do ciclo (`createListTemplate` a partir de um sprint,
 * `instantiateListTemplate` de volta para um sprint novo) são operações de
 * lista, não de tarefa.
 *
 * Contrato do backend: HTTP 200 sempre, status real no corpo
 * (`{ status, message, value }`) — ver aawz_project_management.
 */
class ApiSprints extends APITemplate {
  // ============================================================
  // CRUD da lista
  // ============================================================

  /**
   * @param {object} params
   * @param {string} params.folderId - pasta que recebe a lista
   * @param {object} params.sprint - payload camelCase da lista
   */
  createSprint({ folderId, sprint } = {}) {
    return con.post(endpoint.sprintsByFolder(folderId), sprint)
  }

  /**
   * @param {object} params
   * @param {string} params.sprintId - sprint UUID
   * @param {object} params.data - payload parcial camelCase
   */
  updateSprint({ sprintId, data } = {}) {
    return con.put(endpoint.sprintById(sprintId), data)
  }

  /**
   * @param {object} params
   * @param {string} params.sprintId - sprint UUID
   */
  deleteSprint({ sprintId } = {}) {
    return con.delet(endpoint.sprintById(sprintId))
  }

  /**
   * @param {object} params
   * @param {string} params.folderId - pasta dona das listas
   * @param {string[]} params.orderedIds - ids na ordem desejada
   */
  reorderSprints({ folderId, orderedIds } = {}) {
    return con.patch(endpoint.sprintsReorder, { id_folder: folderId, orderedIds: orderedIds })
  }

  // ============================================================
  // Copiar lista (duplicação com preview)
  // ============================================================

  /**
   * Prévia do que será copiado ao duplicar uma lista — contagens, datas e
   * conflitos. Resposta sob `value`: { totalTasks, doneTasks, openTasks,
   * rootTasks, maxDepth, canCopy, limiteTarefas, limiteProfundidade }.
   * @param {object} params
   * @param {string} params.sprintId - sprint UUID
   */
  getSprintDuplicateSummary({ sprintId } = {}) {
    return con.get(endpoint.sprintDuplicateSummary(sprintId))
  }

  /**
   * Executa a duplicação da lista.
   *
   * As chaves do payload precisam casar EXATAMENTE com o `request_parser_post`
   * do `resource_duplicate_sprint` — chave que não casa cai no default do
   * backend em silêncio, sem erro visível.
   *
   * @param {object} params
   * @param {string} params.sprintId - sprint UUID
   * @param {object} params.payload - { name, folderId, offsetDays,
   *   copyAssignees, copyTags, copyChecklists, copyCustomFields,
   *   includeDoneTasks, copyAcl, resetStatus }
   */
  duplicateSprint({ sprintId, payload } = {}) {
    return con.post(endpoint.sprintDuplicate(sprintId), payload || {})
  }

  // ============================================================
  // Modelos de Lista
  // ============================================================

  /**
   * Lista os Modelos de Lista do escritório.
   * `value` é PAGINADO — `{ items, total, page, limit }`, não um array.
   */
  getListTemplates() {
    return con.get(endpoint.listTemplates)
  }

  /**
   * @param {object} params
   * @param {string} params.id - template UUID
   */
  getListTemplate({ id } = {}) {
    return con.get(endpoint.listTemplateById(id))
  }

  /**
   * Salva uma lista existente como Modelo (gestor-only — backend valida).
   * @param {object} params
   * @param {string} params.sprintId - sprint de origem
   * @param {object} params.payload - { title, description }
   */
  createListTemplate({ sprintId, payload } = {}) {
    return con.post(endpoint.sprintSaveAsTemplate(sprintId), payload || {})
  }

  /**
   * @param {object} params
   * @param {string} params.id - template UUID
   */
  deleteListTemplate({ id } = {}) {
    return con.delet(endpoint.listTemplateById(id))
  }

  /**
   * Instancia um Modelo numa lista NOVA.
   *
   * O backend (`ResourceListTemplateInstantiate`) lê só `name` e a pasta de
   * destino, e sempre cria lista nova — não há como cair numa lista existente.
   * Não mande `sprintId`: o parser não declara o argumento e o flask-restful
   * descarta chave desconhecida sem erro.
   *
   * @param {object} params
   * @param {string} params.templateId - template UUID
   * @param {object} params.payload - { name, folderId }
   */
  instantiateListTemplate({ templateId, payload } = {}) {
    return con.post(endpoint.listTemplateInstantiate(templateId), payload || {})
  }
}

export const apiSprints = new ApiSprints()
